import instantsearch from 'instantsearch.js';
import {
  searchBox,
  configure,
  hits,
  clearRefinements,
  toggleRefinement,
  refinementList,
  stats,
  pagination,
} from 'instantsearch.js/es/widgets';
import searchClient from './searchClient';
import SearchPage from './search-page';

const containers = {
  searchbox: '#global-search-searchbox',
  stats: '#global-search-stats',
  hitsContainer: '#global-search-hits-container',
  hits: '#global-search-hits',
  pagination: '#global-search-pagination',
  toggleRefinementBlog: '#global-search-toggle-refinement-blog',
  refinementListType: '#global-search-refinement-list-type',
  refinementListDepartments: '#global-search-refinement-list-department',
  clearRefinements: '#global-search-clear-refinements',
};

class AlgoliaGlobalSearch extends SearchPage {
  constructor(indexName) {
    super(indexName);

    this.indexName = indexName;
    this.firstLoad = true;

    this.search = instantsearch({
      indexName: this.indexName,
      routing: true,
      searchClient,
      // https://www.algolia.com/doc/api-reference/widgets/instantsearch/js/#widget-param-searchfunction
      searchFunction: helper => {
        // If this is the first load and there is no query param,
        // do not perform a search
        if (this.firstLoad && !this.queryParam) {
          this.firstLoad = false;
          return;
        }

        const { query } = helper.state;
        // Hide the results if there is no query
        this.resultsWrapper.classList.toggle('hide', !query);

        helper.search();
      },
    });

    this.initialize();
    this.search.start();

    // Handle back button route changes
    window.onpopstate = event => {
      // If the input is still focused, change value of input to the current query param
      if (document.activeElement.classList.contains(this.searchInputClassName)) {
        const inputEl = document.querySelector(`.${this.searchInputClassName}`);
        if (event.state) {
          inputEl.value = event.state[this.indexName].query;
        } else {
          inputEl.value = this.queryParam;
        }
      }
    };
  }

  get networkRankingFilters() {
    return [
      `blog_id:${this.blogId}<score=3>`,
      'is_front_page:true<score=2>',
      'blog_id:1<score=1>',
    ];
  }

  initialize() {
    // https://www.algolia.com/doc/api-reference/widgets/js/
    this.search.addWidgets([
      searchBox({
        container: containers.searchbox,
        placeholder: 'Search here',
        showSubmit: false,
        cssClasses: {
          input: this.searchInputClassName,
        },
        templates: {
          reset: `
            <svg class="search__reset">
              <use xlink:href="#icon-x"></use>
            </svg>
          `,
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/configure/js/
      configure({
        hitsPerPage: this.hitsPerPage,
        distinct: true,
        // Force faceting to be applied after de-duplication (via the Distinct setting)
        facetingAfterDistinct: true,
        // Default query to search
        query: this.queryParam,
        // Search the whole multisite network, while ranking current-site records first
        optionalFilters: this.networkRankingFilters,
      }),

      // https://www.algolia.com/doc/api-reference/widgets/stats/js/
      stats({
        container: containers.stats,
        templates: {
          text: data => {
            let count = '';

            const pagination = document.querySelector(containers.pagination);
            this.toggleHide(pagination, data.nbHits <= this.hitsPerPage);

            if (data.hasManyResults) {
              count += `${data.nbHits} results found`;
            } else if (data.hasOneResult) {
              count += `1 result found`;
            } else {
              const noResultsMarkup = this.isMainSite
                ? `<span class="search__stats-no-results">No results found.</span>`
                : `<p class="search__stats-no-results">
                    Can’t find what you’re looking for? <span>Try searching on <a href="/search">the main CSSH site.</a></span>
                  </p>`;
              count += noResultsMarkup;
            }

            return `${count}`;
          },
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/hits/js/
      hits({
        container: containers.hits,
        escapeHTML: false,
        templates: {
          /* eslint-disable */
          item(hit) {
            return `
              <article>
                <a href="${hit.url}">
                  <span class="hit-title">
                    <strong>
                      ${instantsearch.highlight({ attribute: 'title', hit })}
                    </strong>
                    ${
                      hit.content
                        ? `
                    <p class="hit-snippet">
                      ${instantsearch.snippet({ attribute: 'content', hit })}
                    </p>
                    `
                        : ``
                    }
                  </span>
                  <span class="hit-type">
                    ${hit.is_front_page ? 'Department or School' : hit.type}
                  </span>
                </a>
              </article>
            `;
          },
          /* eslint-enable */
        },
        transformItems: items => {
          // Hide pagination and results if no results
          this.toggleHide(this.results, items.length < 1);
          if (window.innerWidth <= 768 && items.length > 0) {
            this.toastActive();
          }
          return items;
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/pagination/js/
      pagination({
        container: containers.pagination,
        showFirst: false,
        showLast: false,
        scrollTo: 'main',
        templates: {
          previous: `
            <span class="visually-hidden">Previous page</span>
            <svg class="arrow prev">
              <use xlink:href="#icon-arrow"></use>
            </svg>
          `,
          next: `
            <span class="visually-hidden">Next page</span>
            <svg class="arrow next">
              <use xlink:href="#icon-arrow"></use>
            </svg>
          `,
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/toggle-refinement/js/
      toggleRefinement({
        container: containers.toggleRefinementBlog,
        attribute: 'blog_id',
        on: 1,
        templates: {
          labelText: 'Only display results on the main CSSH site.',
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/refinement-list/js/
      refinementList({
        container: containers.refinementListType,
        attribute: 'type',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results
          const refList = document
            .querySelector(containers.refinementListType)
            .closest('.js--accordion');
          this.toggleHide(refList, items.length < 1);

          // Change 'Person' label to something more encompassing
          const transformed = items.map(item => ({
            ...item,
            label: item.label === 'person' ? 'Staff & More' : item.label,
          }));

          this.updateRefListClasses(items, refList);

          return transformed;
        },
        // Override template for this refinement list in order to change the 'Person' label
        templates: {
          item: item => this.refinementItemTemplate(item),
        },
      }),

      refinementList({
        container: containers.refinementListDepartments,
        attribute: 'departments',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results
          const refList = document
            .querySelector(containers.refinementListDepartments)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, items.length < 1);
          return items;
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/clear-refinements/js/
      clearRefinements({
        container: containers.clearRefinements,
        templates: {
          resetLabel: 'Clear filters',
        },
      }),
    ]);
  }

  static init() {
    new AlgoliaGlobalSearch();
  }
}

export default AlgoliaGlobalSearch;
